// 사업 지표 표시용 순수 함수. null 은 "미수집"/"—", 0 은 0 으로 그대로 보여 준다.
import { formatAdminAccountMoney } from './account';
import type {
  BusinessAlertItem,
  BusinessCohort,
  BusinessFunnelStep,
  BusinessRevenueCurrency,
} from './businessMetrics';

export const NOT_COLLECTED_LABEL = '미수집';
export const EMPTY_VALUE_LABEL = '—';

/** 통화 최소단위 정수(USD 센트, KRW 원)를 통화 표기로. null 은 미수집. */
export const formatBusinessMoney = (value: number | null, currency: string | null): string =>
  value === null ? NOT_COLLECTED_LABEL : formatAdminAccountMoney(value, currency);

export const formatBusinessCount = (value: number | null): string =>
  value === null ? NOT_COLLECTED_LABEL : value.toLocaleString('ko-KR', { maximumFractionDigits: 1 });

export const formatBusinessPercent = (value: number | null): string =>
  value === null ? EMPTY_VALUE_LABEL : `${value.toLocaleString('ko-KR', { maximumFractionDigits: 1 })}%`;

export const ratioPercent = (part: number | null, whole: number | null): number | null =>
  part === null || whole === null || whole <= 0 ? null : (part / whole) * 100;

export interface RevenueBucketRow {
  period: string;
  payments: string;
  charged: string;
  refunds: string;
  refunded: string;
  net: string;
  netNegative: boolean;
  barPercent: number;
}

export const buildRevenueBucketRows = (currency: BusinessRevenueCurrency): RevenueBucketRow[] => {
  const max = currency.buckets.reduce((acc, bucket) => Math.max(acc, bucket.chargedTotal), 0);
  return currency.buckets.map((bucket) => ({
    period: bucket.period,
    payments: formatBusinessCount(bucket.paymentCount),
    charged: formatBusinessMoney(bucket.chargedTotal, currency.currency),
    refunds: formatBusinessCount(bucket.refundCount),
    refunded: formatBusinessMoney(bucket.refundedTotal, currency.currency),
    net: formatBusinessMoney(bucket.netRevenue, currency.currency),
    netNegative: bucket.netRevenue < 0,
    barPercent: max > 0 ? Math.round((Math.max(0, bucket.chargedTotal) / max) * 100) : 0,
  }));
};

export interface CohortCell {
  offset: number;
  label: string;
  percent: number | null;
}

export interface CohortTable {
  offsets: number[];
  rows: Array<{ cohortMonth: string; started: number; cells: CohortCell[] }>;
}

/** retained[k] = 시작 k개월 뒤에도 유지. 경과하지 않은 달은 빈 칸으로 둔다. */
export const buildCohortTable = (cohorts: BusinessCohort[]): CohortTable => {
  const width = cohorts.reduce((acc, cohort) => Math.max(acc, cohort.retained.length), 0);
  const offsets = Array.from({ length: width }, (_, index) => index);
  return {
    offsets,
    rows: cohorts.map((cohort) => ({
      cohortMonth: cohort.cohortMonth,
      started: cohort.started,
      cells: offsets.map((offset) => {
        if (offset >= cohort.retained.length) {
          return { offset, label: '', percent: null };
        }
        const retained = cohort.retained[offset] ?? 0;
        const percent = ratioPercent(retained, cohort.started);
        return { offset, label: `${formatBusinessCount(retained)} (${formatBusinessPercent(percent)})`, percent };
      }),
    })),
  };
};

export interface FunnelStepRow {
  key: string;
  label: string;
  collected: boolean;
  countLabel: string;
  conversionLabel: string;
  source: string;
  note: string | null;
}

/** 전환율은 바로 앞 단계와 이 단계가 모두 수집됐고 숫자가 있을 때만 계산한다. */
export const buildFunnelStepRows = (steps: BusinessFunnelStep[]): FunnelStepRow[] =>
  steps.map((step, index) => {
    const previous = index > 0 ? steps[index - 1] : undefined;
    const comparable = Boolean(previous?.collected && step.collected);
    const conversion = comparable ? ratioPercent(step.count, previous?.count ?? null) : null;
    return {
      key: step.key,
      label: step.label,
      collected: step.collected,
      countLabel: step.collected ? formatBusinessCount(step.count) : NOT_COLLECTED_LABEL,
      conversionLabel: formatBusinessPercent(conversion),
      source: step.source,
      note: step.note,
    };
  });

export const formatPlanMix = (monthly: number, yearly: number): string => {
  const total = monthly + yearly;
  if (total <= 0) {
    return `월 ${formatBusinessCount(monthly)} · 연 ${formatBusinessCount(yearly)}`;
  }
  return `월 ${formatBusinessCount(monthly)} (${formatBusinessPercent(ratioPercent(monthly, total))})`
    + ` · 연 ${formatBusinessCount(yearly)} (${formatBusinessPercent(ratioPercent(yearly, total))})`;
};

const ALERT_ORDER: Record<BusinessAlertItem['level'], number> = { critical: 0, warning: 1, info: 2 };

export const sortAlertItems = (items: BusinessAlertItem[]): BusinessAlertItem[] =>
  [...items].sort((left, right) => ALERT_ORDER[left.level] - ALERT_ORDER[right.level]);

export const formatBusinessDateTime = (value: string | null): string => {
  if (!value) return EMPTY_VALUE_LABEL;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? value : parsed.toLocaleString('ko-KR', { timeZone: 'Asia/Seoul' });
};
